import { axiosApi } from './api'
import {
    invalidateIndicators,
    getFileIndicators,
    recacheIndicators,
} from 'features/patient-file-thumbnail/thumbnailSlice'

const type = 'Tag'

export const tags = axiosApi.injectEndpoints({
    endpoints: build => ({
        /*
         * Get all the tags
         */
        getTags: build.query({
            query: arg => `v1/tags?${arg?.query || ''}`,
            transformResponse: res => res.data,
            providesTags: (result, error, arg) => {
                if (!result) return null
                if (arg?.extraArgs?.id) {
                    return [
                        { type, id: 'PARTIAL' },
                        { type, id: arg.extraArgs.id },
                    ]
                } else return [{ type, id: 'LIST' }]
            },
            keepUnusedDataFor: 600,
        }),

        /*
         * Get all the tags with aggregate of 'all' and 'some'.
         * Used to get the existing tags before adding or deleting tag for single or multiple files
         */
        getTagsAggregate: build.query({
            query: query => `v1/tags/_aggregate?${query}`,
            transformResponse: res => res.data,
            providesTags: result => (result ? ['AggregateTags'] : null),
        }),

        /*
         * Add a new tag
         */
        addTag: build.mutation({
            query: ({ body }) => ({
                url: `v1/tags/_add`,
                method: 'PUT',
                body,
            }),
            async onQueryStarted({ body, query }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    axiosApi.util.updateQueryData('getTagsAggregate', query, draft => {
                        if (
                            !draft['any'].find(
                                tag => tag.toLowerCase() === body.tag.toLowerCase()
                            ) &&
                            !draft['all'].find(tag => tag.toLowerCase() === body.tag.toLowerCase())
                        ) {
                            draft.all.push(body.tag)
                        }
                    })
                )
                try {
                    await queryFulfilled
                    await dispatch(
                        axiosApi.util.updateQueryData('getTags', undefined, draft => {
                            const index = draft.findIndex(tag => tag === body.tag)
                            if (index === -1) draft.push(body.tag)
                        })
                    )
                    if (body.fileIds) {
                        await dispatch(invalidateIndicators(body.fileIds.split(',')))
                        dispatch(getFileIndicators({ ids: body.fileIds }))
                    } else {
                        dispatch(recacheIndicators())
                    }
                } catch {
                    patchResult.undo()
                }
            },
            invalidatesTags: [{ type, id: 'PARTIAL' }, 'AggregateTags'],
            transformResponse: res => res.data,
        }),

        /*
         * Delete a tag
         */
        deleteTag: build.mutation({
            query: ({ body }) => ({
                url: `v1/tags/_remove`,
                method: 'PUT',
                body,
            }),
            async onQueryStarted({ body, query }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    axiosApi.util.updateQueryData('getTagsAggregate', query, draft => {
                        if (draft['any'].find(tag => tag === body.tag))
                            draft['any'].splice(draft['any'].indexOf(body.tag), 1)
                        if (draft['all'].find(tag => tag === body.tag))
                            draft['all'].splice(draft['all'].indexOf(body.tag), 1)
                    })
                )
                try {
                    await queryFulfilled
                    if (body.fileIds) {
                        await dispatch(invalidateIndicators(body.fileIds.split(',')))
                        dispatch(getFileIndicators({ ids: body.fileIds }))
                    } else {
                        dispatch(recacheIndicators())
                    }
                } catch {
                    patchResult.undo()
                }
            },
            invalidatesTags: [type, 'AggregateTags', 'PatientFiles'],
            transformResponse: res => res.data,
        }),

        /*
         * Auto suggestion/search tags
         */
        getTagsSuggestions: build.query({
            query: ({ keyword }) => `v1/tags?Query=tags=co:"${keyword}"`,
            transformResponse: res => {
                return res.data.length
                    ? [
                          {
                              type: 'tag',
                              data: res.data.map((t, i) => {
                                  return {
                                      id: i + 1,
                                      name: t,
                                  }
                              }),
                          },
                      ]
                    : []
            },

            keepUnusedDataFor: 600,
        }),
    }),
})

export const {
    useGetTagsQuery,
    useGetTagsAggregateQuery,
    useAddTagMutation,
    useDeleteTagMutation,
    useGetTagsSuggestionsQuery,
} = tags
